<template>
  <div class="container-fluid">
    <UITabs v-model="tab">
      <UITabsList v-if="!restrict_to">
        <UITabsTrigger value="mash_events">Mash Events</UITabsTrigger>
        <UITabsTrigger value="webinars">Webinars</UITabsTrigger>
      </UITabsList>

      <UITabsContent value="mash_events">
        <div class="row mb-4">
          <div class="col-12 col-lg">
            <div class="input-group">
              <span class="input-group-text">
                <i class="fa fa-search text-primary" aria-hidden="true"></i>
              </span>
              <input
                class="form-control"
                placeholder="Enter your postcode to search for an event near you"
                v-model="postcode"
              />
            </div>
          </div>

          <div class="col-12 col-lg-auto mt-2 mt-lg-0">
            <div
              class="btn-group w-100"
              role="group"
              aria-label="Toggle map view"
            >
              <UIButton
                variant="success"
                :active="showMap"
                @click="showMap = true"
              >
                Map View
              </UIButton>
              <UIButton
                variant="success"
                :active="showMap === false"
                @click="showMap = false"
              >
                List View
              </UIButton>
            </div>
          </div>
        </div>

        <EventsMap
          v-show="showMap"
          v-model:selected="selectedMarkerId"
          :events="liveEvents"
          :center="mapCenter"
          :zoom="mapZoom"
        />

        <EventsTable
          v-show="!showMap"
          event-type="mash_events"
          :events="liveEvents"
          @pan-to-event="panToEvent"
        />
      </UITabsContent>

      <UITabsContent value="webinars">
        <EventsTable event-type="webinars" :events="webinars" />
      </UITabsContent>
    </UITabs>
  </div>
</template>

<script lang="ts">
import { watchDebounced } from "@vueuse/core";
import {
  CPDEvent,
  fetchEvents,
  getDistanceMiles,
  getPostcodeCoordinates,
} from "./utils";

export type CPDEventType = "mash_events" | "webinars";

export type LiveCPDEvent = Omit<CPDEvent, "status"> &
  Required<Pick<CPDEvent, "address" | "coord">> & { status: "live" };

export type Webinar = Omit<CPDEvent, "status" | "address"> & { status: "live" };

type Props = { restrict_to: CPDEventType };
</script>

<script setup lang="ts">
import UIButton from "@/components/UI/UIButton.vue";
import UITabs from "@/components/UI/tabs/UITabs.vue";
import UITabsContent from "@/components/UI/tabs/UITabsContent.vue";
import UITabsList from "@/components/UI/tabs/UITabsList.vue";
import UITabsTrigger from "@/components/UI/tabs/UITabsTrigger.vue";
import { computed, onBeforeMount, ref } from "vue";
import EventsMap, { Coordinate } from "./EventsMap.vue";
import EventsTable from "./EventsTable.vue";

const MAP_CENTER: Coordinate = { lat: 54.00366, lng: -2.547855 };

// Props
const props = defineProps<Props>();

// Map refs
const mapCenter = ref<Coordinate>(MAP_CENTER);
const mapZoom = ref<number>(5);
const showMap = ref<boolean>(false);
const selectedMarkerId = ref<string | null>(null);

const tab = ref<CPDEventType>(props.restrict_to ?? "mash_events");
const postcode = ref<string>("");
const events = ref<CPDEvent[]>([]);

const liveEvents = computed<LiveCPDEvent[]>(() =>
  events.value.filter(isLiveEvent).sort(sortByDistanceAndTime)
);
const webinars = computed<Webinar[]>(() =>
  events.value.filter(isWebinar).sort(sortByStartTime)
);

onBeforeMount(() => {
  loadEvents();
});

watchDebounced(
  postcode,
  (value) => {
    assignEventsDistances(value);

    if (liveEvents.value.length && showMap) {
      panToEvent(liveEvents.value[0]);
    }
  },
  { debounce: 1000 }
);

/**
 * Assign to each event the distance from the given postcode.
 */
async function assignEventsDistances(postcode: string) {
  const postcodeCoord = await getPostcodeCoordinates(postcode);

  events.value = events.value.map((e) => {
    if (!isLiveEvent(e)) {
      return e;
    }

    const distance = postcodeCoord
      ? getDistanceMiles(postcodeCoord, e.coord)
      : undefined;

    return { ...e, distance };
  });
}

/**
 * Fetch events from server.
 */
async function loadEvents() {
  try {
    events.value = await fetchEvents();
  } catch (e) {
    console.error(e);
  }
}

/**
 * Open map tab, pan to the given event and open info window.
 */
function panToEvent(event: LiveCPDEvent) {
  showMap.value = true;
  mapCenter.value = event.coord;
  mapZoom.value = 15;
  selectedMarkerId.value = event.id;
}

function isLiveEvent(event: CPDEvent): event is LiveCPDEvent {
  return !event.online;
}
function isWebinar(event: CPDEvent): event is Webinar {
  return event.online;
}
function sortByStartTime(a: CPDEvent, b: CPDEvent) {
  return a.startTime.getTime() - b.startTime.getTime();
}
function sortByDistanceAndTime(a: LiveCPDEvent, b: LiveCPDEvent) {
  return a.distance && b.distance
    ? a.distance - b.distance
    : a.startTime.getTime() - b.startTime.getTime();
}
</script>
